import React, { useState, useEffect } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { useSelector, useDispatch } from 'react-redux';
import { updatePartnerProfile } from '../../api/partner';
import { UPDATE_PARTNER_INFO } from '../../features/partner/partnerSlice';
import { setPartnerInfo } from '../../utils/partnerInfo';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CompanyProfile = () => {
  const { setHeaderTitle } = useHeader();
  const dispatch = useDispatch();
  const { partnerInfo } = useSelector((state) => state.partner);

  // Company Details
  const [companyData, setCompanyData] = useState({
    companyName: '',
    industryOrSector: '',
    description: '',
    branchAddress: '',
    phone: '',
    email: '',
    headquarterAddress: '',
    websiteUrl: '',
    registrationNumber: '',
    taxIdentificationNumber: ''
  });

  // Account Details
  const [accountData, setAccountData] = useState({
    username: '',
    displayName: '',
    email: ''
  });

  // Point of Contact
  const [pocData, setPocData] = useState({
    pocName: '',
    pocDesignation: '',
    pocDepartment: '',
    pocPhone: '',
    pocMobile: '',
    pocEmail: ''
  });

  // Social Media Links
  const [socialData, setSocialData] = useState({
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    youtube: ''
  });

  // Partnership Details
  const [partnershipData, setPartnershipData] = useState({
    typeOfPartnership: [],
    partnershipObjective: '',
    partnershipStartDate: '',
    categoryOfChallengePreferred: '',
    rewardsOffered: '',
    rewardsMode: '',
    advertisementType: '',
    preferredPlacementSlot: '',
    advertisementDetails: '',
    sponsorMessage: '',
    numberOfProductsOnOffer: '',
    typeOfProductsOnOffer: '',
    priceRangeOfProductsOnOffer: '',
    detailsOfDonationPreference: '',
    detailsOfSpecificProjects: '',
    detailsOfCSRGoals: ''
  });

  // Brand Promotion
  const [brandPromotion, setBrandPromotion] = useState({
    categoryOfProducts: '',
    priceRangeOfProducts: '',
    detailsOfProducts: '',
    offerType: '',
    advertisementType: '',
    advertisementDetails: '',
    preferredPlacementSlot: '',
    brandMessage: ''
  });

  // Project Promotion
  const [projectPromotion, setProjectPromotion] = useState({
    categoryOfProjects: '',
    numberOfProjects: '',
    locationOfProjects: '',
    detailsOfProjects: '',
    fundRaiseTarget: '',
    platformFeeAgreement: false,
    developerMessage: ''
  });

  // CSR Donation
  const [csrDonation, setCsrDonation] = useState({
    categoryOfProjectsPreferred: '',
    donationMatchingRatio: '',
    detailsOfProjectsPreferred: '',
    detailsOfCSRESGGoals: '',
    detailsAboutDonationBudget: '',
    donorMessage: ''
  });

  // Advertiser
  const [advertiser, setAdvertiser] = useState({
    advertisementCommodity: '',
    advertisementGoal: '',
    advertiserAdvertisementType: '',
    advertiserPreferredPlacementSlot: '',
    advertiserAdvertisementDetails: '',
    advertisementBudget: '',
    targetDemographicsCriteria: '',
    targetDemographicValues: ''
  });

  // Financial Details
  const [financialData, setFinancialData] = useState({
    bankAccountDetails: '',
    paymentTerms: '',
    agreementOnRevenueShareOrCommissionRates: ''
  });

  // Impact & Reporting
  const [impactData, setImpactData] = useState({
    metricsDataPoints: '',
    reportingFrequency: 'Monthly',
    aShortParagraphOnTheSponsorCompany: '',
    reportingGoals: '',
    remarksOrNotes: ''
  });

  // Account Status
  const [accountStatus, setAccountStatus] = useState({
    status: 'Active',
    lastUpdated: '',
    createdAt: ''
  });

  // Change Password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    setHeaderTitle('Profile & Settings');

    if (partnerInfo) {
      // Populate Company Details
      setCompanyData({
        companyName: partnerInfo.companyName || '',
        industryOrSector: partnerInfo.industryOrSector || '',
        description: partnerInfo.description || '',
        branchAddress: partnerInfo.branchAddress || '',
        phone: partnerInfo.phone || '',
        email: partnerInfo.email || '',
        headquarterAddress: partnerInfo.headquarterAddress || '',
        websiteUrl: partnerInfo.websiteUrl || '',
        registrationNumber: partnerInfo.registrationNumber || '',
        taxIdentificationNumber: partnerInfo.taxIdentificationNumber || ''
      });

      // Populate Account Details
      setAccountData({
        username: partnerInfo.accountDetails?.username || '',
        displayName: partnerInfo.accountDetails?.displayName || partnerInfo.companyName || '',
        email: partnerInfo.accountDetails?.emails?.[0] || partnerInfo.email || ''
      });

      // Populate Point of Contact
      setPocData({
        pocName: partnerInfo.pointOfContact?.pocName || '',
        pocDesignation: partnerInfo.pointOfContact?.pocDesignation || '',
        pocDepartment: partnerInfo.pointOfContact?.pocDepartment || '',
        pocPhone: partnerInfo.pointOfContact?.pocPhone || '',
        pocMobile: partnerInfo.pointOfContact?.pocMobile || '',
        pocEmail: partnerInfo.pointOfContact?.pocEmail || ''
      });

      // Populate Social Media Links
      setSocialData({
        linkedin: partnerInfo.socialLinks?.linkedIn || '',
        facebook: partnerInfo.socialLinks?.facebook || '',
        twitter: partnerInfo.socialLinks?.twitter || '',
        instagram: partnerInfo.socialLinks?.instagram || '',
        youtube: partnerInfo.socialLinks?.youtube || ''
      });

      // Populate Partnership Details
      setPartnershipData({
        typeOfPartnership: partnerInfo.partnershipDetails?.typeOfPartnership || [],
        partnershipObjective: partnerInfo.partnershipDetails?.partnershipObjective || '',
        partnershipStartDate: partnerInfo.partnershipDetails?.partnershipStartDate || '',
        categoryOfChallengePreferred: partnerInfo.partnershipDetails?.categoryOfChallengePreferred || '',
        rewardsOffered: partnerInfo.partnershipDetails?.rewardsOffered || '',
        rewardsMode: partnerInfo.partnershipDetails?.rewardsMode || '',
        advertisementType: partnerInfo.partnershipDetails?.advertisementType || '',
        preferredPlacementSlot: partnerInfo.partnershipDetails?.preferredPlacementSlot || '',
        advertisementDetails: partnerInfo.partnershipDetails?.advertisementDetails || '',
        sponsorMessage: partnerInfo.partnershipDetails?.sponsorMessage || '',
        numberOfProductsOnOffer: partnerInfo.partnershipDetails?.numberOfProductsOnOffer || '',
        typeOfProductsOnOffer: partnerInfo.partnershipDetails?.typeOfProductsOnOffer || '',
        priceRangeOfProductsOnOffer: partnerInfo.partnershipDetails?.priceRangeOfProductsOnOffer || '',
        detailsOfDonationPreference: partnerInfo.partnershipDetails?.detailsOfDonationPreference || '',
        detailsOfSpecificProjects: partnerInfo.partnershipDetails?.detailsOfSpecificProjects || '',
        detailsOfCSRGoals: partnerInfo.partnershipDetails?.detailsOfCSRGoals || ''
      });

      // Populate Brand Promotion
      setBrandPromotion({
        categoryOfProducts: partnerInfo.brandPromotion?.categoryOfProducts || '',
        priceRangeOfProducts: partnerInfo.brandPromotion?.priceRangeOfProducts || '',
        detailsOfProducts: partnerInfo.brandPromotion?.detailsOfProducts || '',
        offerType: partnerInfo.brandPromotion?.offerType || '',
        advertisementType: partnerInfo.brandPromotion?.advertisementType || '',
        advertisementDetails: partnerInfo.brandPromotion?.advertisementDetails || '',
        preferredPlacementSlot: partnerInfo.brandPromotion?.preferredPlacementSlot || '',
        brandMessage: partnerInfo.brandPromotion?.brandMessage || ''
      });

      // Populate Project Promotion
      setProjectPromotion({
        categoryOfProjects: partnerInfo.projectPromotion?.categoryOfProjects || '',
        numberOfProjects: partnerInfo.projectPromotion?.numberOfProjects || '',
        locationOfProjects: partnerInfo.projectPromotion?.locationOfProjects || '',
        detailsOfProjects: partnerInfo.projectPromotion?.detailsOfProjects || '',
        fundRaiseTarget: partnerInfo.projectPromotion?.fundRaiseTarget || '',
        platformFeeAgreement: partnerInfo.projectPromotion?.platformFeeAgreement || false,
        developerMessage: partnerInfo.projectPromotion?.developerMessage || ''
      });

      // Populate CSR Donation
      setCsrDonation({
        categoryOfProjectsPreferred: partnerInfo.csrDonation?.categoryOfProjectsPreferred || '',
        donationMatchingRatio: partnerInfo.csrDonation?.donationMatchingRatio || '',
        detailsOfProjectsPreferred: partnerInfo.csrDonation?.detailsOfProjectsPreferred || '',
        detailsOfCSRESGGoals: partnerInfo.csrDonation?.detailsOfCSRESGGoals || '',
        detailsAboutDonationBudget: partnerInfo.csrDonation?.detailsAboutDonationBudget || '',
        donorMessage: partnerInfo.csrDonation?.donorMessage || ''
      });

      // Populate Advertiser
      setAdvertiser({
        advertisementCommodity: partnerInfo.advertiser?.advertisementCommodity || '',
        advertisementGoal: partnerInfo.advertiser?.advertisementGoal || '',
        advertiserAdvertisementType: partnerInfo.advertiser?.advertiserAdvertisementType || '',
        advertiserPreferredPlacementSlot: partnerInfo.advertiser?.advertiserPreferredPlacementSlot || '',
        advertiserAdvertisementDetails: partnerInfo.advertiser?.advertiserAdvertisementDetails || '',
        advertisementBudget: partnerInfo.advertiser?.advertisementBudget || '',
        targetDemographicsCriteria: partnerInfo.advertiser?.targetDemographicsCriteria || '',
        targetDemographicValues: partnerInfo.advertiser?.targetDemographicValues || ''
      });

      // Populate Financial Details
      setFinancialData({
        bankAccountDetails: partnerInfo.financialDetails?.bankAccountDetails || '',
        paymentTerms: partnerInfo.financialDetails?.paymentTerms || '',
        agreementOnRevenueShareOrCommissionRates: partnerInfo.financialDetails?.agreementOnRevenueShareOrCommissionRates || ''
      });

      // Populate Impact & Reporting
      setImpactData({
        metricsDataPoints: partnerInfo.impactAndReporting?.metricsDataPoints || '',
        reportingFrequency: partnerInfo.impactAndReporting?.reportingFrequency || 'Monthly',
        aShortParagraphOnTheSponsorCompany: partnerInfo.impactAndReporting?.aShortParagraphOnTheSponsorCompany || '',
        reportingGoals: partnerInfo.impactAndReporting?.reportingGoals || '',
        remarksOrNotes: partnerInfo.impactAndReporting?.remarksOrNotes || ''
      });

      // Populate Account Status
      setAccountStatus({
        status: partnerInfo.active ? 'Active' : 'Inactive',
        lastUpdated: partnerInfo.updatedAt ? new Date(partnerInfo.updatedAt).toLocaleDateString() : '',
        createdAt: partnerInfo.createdAt ? new Date(partnerInfo.createdAt).toLocaleDateString() : ''
      });

      if (partnerInfo.logo) {
        setImagePreview(partnerInfo.logo);
      }
    }
  }, [setHeaderTitle, partnerInfo]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long!');
      return;
    }

    setLoading(true);
    try {
      // Call password change API
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.info('Profile updates are managed by administrators. Please contact support for changes.');
  };

  // Check if a specific partnership type is selected
  const hasPartnershipType = (type) => {
    return Array.isArray(partnershipData.typeOfPartnership) && partnershipData.typeOfPartnership.includes(type);
  };

  return (
    <div className='w-full p-6'>
      {/* Header Section with Profile Image */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
        <div className='p-6'>
          <div className='flex items-start gap-6'>
            <div className='flex-shrink-0'>
              <div className='w-20 h-20 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-2xl'>
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile" className='w-20 h-20 rounded-full object-cover' />
                ) : (
                  <span>CO</span>
                )}
              </div>
            </div>
            <div className='flex-grow'>
              <h2 className='text-xl font-semibold text-gray-900'>{accountData.displayName || companyData.companyName}</h2>
              <p className='text-sm text-gray-500 mt-1'>@{accountData.username}</p>
              <span className={`inline-block mt-2 px-3 py-1 ${accountStatus.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} text-xs font-semibold rounded`}>
                {accountStatus.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Company Information
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Company Name:</div>
                <div className='text-sm text-gray-900'>{companyData.companyName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Industry/Sector:</div>
                <div className='text-sm text-gray-900'>{companyData.industryOrSector || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Phone:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{companyData.phone || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Email:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{companyData.email || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Branch Address:</div>
                <div className='text-sm text-gray-500'>{companyData.branchAddress || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Headquarter Address:</div>
                <div className='text-sm text-gray-500'>{companyData.headquarterAddress || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Website URL:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {companyData.websiteUrl ? (
                    <a href={companyData.websiteUrl} target="_blank" rel="noopener noreferrer">{companyData.websiteUrl}</a>
                  ) : (
                    'Not provided'
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Registration Number:</div>
                <div className='text-sm text-gray-900'>{companyData.registrationNumber || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Tax ID Number:</div>
                <div className='text-sm text-gray-900'>{companyData.taxIdentificationNumber || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Description:</div>
                <div className='text-sm text-gray-900'>{companyData.description || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Account Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Username:</div>
                <div className='text-sm text-gray-900'>{accountData.username || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Display Name:</div>
                <div className='text-sm text-gray-900'>{accountData.displayName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Email Address:</div>
                <div className='text-sm text-gray-900'>{accountData.email || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Point of Contact */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Point of Contact
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>POC Name:</div>
                <div className='text-sm text-gray-900'>{pocData.pocName || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Phone:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocPhone || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Designation:</div>
                <div className='text-sm text-gray-900'>{pocData.pocDesignation || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Mobile:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocMobile || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Department:</div>
                <div className='text-sm text-gray-500'>{pocData.pocDepartment || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Email:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>{pocData.pocEmail || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Social Media Links
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>LinkedIn:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.linkedin ? (
                    <a href={socialData.linkedin} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Instagram:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.instagram ? (
                    <a href={socialData.instagram} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Facebook:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.facebook ? (
                    <a href={socialData.facebook} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>YouTube:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.youtube ? (
                    <a href={socialData.youtube} target="_blank" rel="noopener noreferrer">View Channel</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Twitter:</div>
                <div className='text-sm text-blue-600 hover:underline cursor-pointer'>
                  {socialData.twitter ? (
                    <a href={socialData.twitter} target="_blank" rel="noopener noreferrer">View Profile</a>
                  ) : (
                    <span className='text-gray-500'>Not provided</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partnership Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Partnership Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Type of Partnership:</div>
                <div className='text-sm text-gray-900'>{Array.isArray(partnershipData.typeOfPartnership) && partnershipData.typeOfPartnership.length > 0 ? partnershipData.typeOfPartnership.join(', ') : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Partnership Objective:</div>
                <div className='text-sm text-gray-900'>{partnershipData.partnershipObjective || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Partnership Start Date:</div>
                <div className='text-sm text-gray-900'>{partnershipData.partnershipStartDate ? new Date(partnershipData.partnershipStartDate).toLocaleDateString() : 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Category of Challenge:</div>
                <div className='text-sm text-gray-900'>{partnershipData.categoryOfChallengePreferred || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Rewards Offered:</div>
                <div className='text-sm text-gray-900'>{partnershipData.rewardsOffered || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Rewards Mode:</div>
                <div className='text-sm text-gray-900'>{partnershipData.rewardsMode || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Advertisement Type:</div>
                <div className='text-sm text-gray-900'>{partnershipData.advertisementType || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Preferred Placement Slot:</div>
                <div className='text-sm text-gray-900'>{partnershipData.preferredPlacementSlot || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Advertisement Details:</div>
                <div className='text-sm text-gray-900'>{partnershipData.advertisementDetails || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Sponsor Message:</div>
                <div className='text-sm text-gray-900'>{partnershipData.sponsorMessage || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Brand/Product Promotion - Conditional */}
        {hasPartnershipType('Brand/Product Promotion') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Brand/Product Promotion Details
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Category of Products:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.categoryOfProducts || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Price Range:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.priceRangeOfProducts || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Offer Type:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.offerType || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Preferred Placement:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.preferredPlacementSlot || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Product Details:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.detailsOfProducts || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Brand Message:</div>
                  <div className='text-sm text-gray-900'>{brandPromotion.brandMessage || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Promotion - Conditional */}
        {hasPartnershipType('Plant Trees') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Project Promotion Details
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Category of Projects:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.categoryOfProjects || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Number of Projects:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.numberOfProjects || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Location:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.locationOfProjects || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Fund Raise Target:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.fundRaiseTarget || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Platform Fee Agreement:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.platformFeeAgreement ? 'Yes' : 'No'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Project Details:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.detailsOfProjects || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Developer Message:</div>
                  <div className='text-sm text-gray-900'>{projectPromotion.developerMessage || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSR/ESG Donation - Conditional */}
        {hasPartnershipType('CSR/ESG') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                CSR/ESG Donation Details
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Category Preferred:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.categoryOfProjectsPreferred || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Donation Matching Ratio:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.donationMatchingRatio || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Projects Preferred:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.detailsOfProjectsPreferred || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>CSR/ESG Goals:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.detailsOfCSRESGGoals || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Donation Budget:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.detailsAboutDonationBudget || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Donor Message:</div>
                  <div className='text-sm text-gray-900'>{csrDonation.donorMessage || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advertiser - Conditional */}
        {hasPartnershipType('Advertiser') && (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
            <div className='p-6'>
              <h3 className='text-base font-semibold text-gray-900 mb-4'>
                Advertiser Details
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Advertisement Commodity:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertisementCommodity || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Advertisement Goal:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertisementGoal || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Advertisement Type:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertiserAdvertisementType || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Preferred Placement:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertiserPreferredPlacementSlot || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Advertisement Budget:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertisementBudget || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='text-sm text-gray-600'>Target Demographics:</div>
                  <div className='text-sm text-gray-900'>{advertiser.targetDemographicsCriteria || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Target Values:</div>
                  <div className='text-sm text-gray-900'>{advertiser.targetDemographicValues || 'Not provided'}</div>
                </div>
                <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                  <div className='text-sm text-gray-600'>Advertisement Details:</div>
                  <div className='text-sm text-gray-900'>{advertiser.advertiserAdvertisementDetails || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Details */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Financial Details
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Bank Account Details:</div>
                <div className='text-sm text-gray-500'>{financialData.bankAccountDetails || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Payment Terms:</div>
                <div className='text-sm text-gray-500'>{financialData.paymentTerms || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Revenue Share Agreement:</div>
                <div className='text-sm text-gray-500'>{financialData.agreementOnRevenueShareOrCommissionRates || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Impact & Reporting */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Impact & Reporting
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Metrics Data Points:</div>
                <div className='text-sm text-gray-900'>{impactData.metricsDataPoints || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Reporting Frequency:</div>
                <div className='text-sm text-gray-900'>{impactData.reportingFrequency}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Company Description:</div>
                <div className='text-sm text-gray-900'>{impactData.aShortParagraphOnTheSponsorCompany || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Reporting Goals:</div>
                <div className='text-sm text-gray-900'>{impactData.reportingGoals || 'Not provided'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4 md:col-span-2'>
                <div className='text-sm text-gray-600'>Remarks/Notes:</div>
                <div className='text-sm text-gray-900'>{impactData.remarksOrNotes || 'Not provided'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Status */}
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
          <div className='p-6'>
            <h3 className='text-base font-semibold text-gray-900 mb-4'>
              Account Status
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Account Status:</div>
                <div className='text-sm'>
                  <span className={`inline-block px-3 py-1 ${accountStatus.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} text-xs font-semibold rounded`}>
                    {accountStatus.status}
                  </span>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Last Updated:</div>
                <div className='text-sm text-gray-900'>{accountStatus.lastUpdated || 'Not available'}</div>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div className='text-sm text-gray-600'>Created At:</div>
                <div className='text-sm text-gray-900'>{accountStatus.createdAt || 'Not available'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password section removed - use the one in the top-right dropdown menu instead */}
      </form>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
      />
    </div>
  );
};

export default CompanyProfile;
